import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { RadioV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import { Caption } from '@/components/@IntranetLibrary/Typography';
import {
  parseHireKind,
  switchHireSubject,
  type HireKind,
} from '@/utils/approval/formSubject';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';

const HIRE_KIND_OPTIONS: { value: HireKind; label: string }[] = [
  { value: 'new', label: '신규' },
  { value: 'extension', label: '연장' },
];

type HireKindFieldProps = {
  apvType: number;
};

/**
 * 채용 품의(219·220) 「신규/연장 여부」 — 고르면 제목의 신규/연장 표시를 바꿈.
 * 선택값은 저장하지 않으므로, 값을 선택하기 전에는 제목에서 가져옴(편집 진입 복원)
 */
const HireKindField = ({ apvType }: HireKindFieldProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<ApprovalFormValues>();
  const subject = watch('subject');
  const [picked, setPicked] = useState<HireKind | null>(null);
  const kind = picked ?? parseHireKind(subject ?? '');

  const handleChange = (next: HireKind) => {
    setPicked(next);
    setValue('subject', switchHireSubject(apvType, subject ?? '', next));
  };

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('신규/연장 여부')}</InfoSectionTitle>
      </FieldHeader>
      <RadioGroup
        row
        value={kind}
        onChange={(e) => handleChange(e.target.value as HireKind)}
      >
        {HIRE_KIND_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.value}
            value={opt.value}
            control={<RadioV2 />}
            label={
              <Typography variant="body2">
                {t(opt.label, { context: 'hire' })}
              </Typography>
            }
          />
        ))}
      </RadioGroup>
      {kind === 'extension' && (
        <Caption>{t('※ 연장의 경우 이전 품의 첨부 필수')}</Caption>
      )}
    </FieldBlock>
  );
};

export default HireKindField;
